// Transcript rendering for the demo page: one bubble per user turn and per
// assistant turn, driven only by `onTranscript` events and the element they are
// painted into. Split out of main.ts so the ordering rules below can be tested
// without the rest of the page.

import type { TranscriptEvent } from "../src/index";

export interface TranscriptView {
	/** Folds one transcript event into the rendered conversation. */
	render(e: TranscriptEvent): void;
	/** Appends a standalone line, e.g. an error. */
	line(who: string, cls: string, text: string): void;
	/** Drops every line: a new session starts from an empty transcript. */
	clear(): void;
}

/** Builds a view that renders into `container`. */
export function createTranscriptView(container: HTMLElement): TranscriptView {
	const doc = container.ownerDocument;
	/** The in-flight interim user line, promoted in place when the final arrives. */
	let interim: HTMLElement | null = null;
	/** One line per assistant turn, rewritten as cumulative text grows. */
	const assistant = new Map<string, HTMLElement>();
	/** One line per committed user turn, so an amendment can correct it in place. */
	const user = new Map<string, HTMLElement>();
	/** Backend and agent rows already on screen, by `messageId`. */
	const context = new Set<string>();

	const bubble = (who: string, cls: string): HTMLElement => {
		const wrap = doc.createElement("p");
		wrap.className = `msg ${cls}`;
		const label = doc.createElement("span");
		label.className = "who";
		label.textContent = who;
		const body = doc.createElement("span");
		body.className = "body";
		wrap.append(label, doc.createElement("br"), body);
		container.appendChild(wrap);
		container.scrollTop = container.scrollHeight;
		return wrap;
	};

	const setBody = (wrap: HTMLElement, text: string): void => {
		const body = wrap.querySelector(".body");
		if (body) body.textContent = text;
		container.scrollTop = container.scrollHeight;
	};

	return {
		render(e: TranscriptEvent): void {
			if (e.role === "user") {
				if (e.amend) {
					// A re-recognised word in a turn already on screen. An id we
					// never rendered (reconnect, late join) has nothing to fix.
					const line = e.messageId ? user.get(e.messageId) : undefined;
					if (line) {
						setBody(line, e.text);
						line.classList.add("amended");
					}
					return;
				}
				if (!e.final && e.text === "") {
					// The server withdrew the interim: it discarded that
					// pending text, so no final is coming and the line belongs
					// to no turn.
					interim?.remove();
					interim = null;
					return;
				}
				if (e.final && e.messageId) {
					// A resumed or re-attached session replays its finals (SPEC
					// §10, §13) and the SDK keys them by `messageId` — a repeat
					// corrects that turn in its history rather than appending a
					// second one. The view has to agree, or the same words land
					// on screen twice under the resumed session.
					const known = user.get(e.messageId);
					if (known) {
						setBody(known, e.text);
						return;
					}
				}
				if (!interim) interim = bubble("you", "user interim");
				setBody(interim, e.text);
				if (e.final) {
					interim.classList.remove("interim");
					if (e.messageId) user.set(e.messageId, interim);
					interim = null;
				}
				return;
			}
			if (e.role === "tool") {
				// The job log (SPEC §9): one line per transition, so a slow tool
				// stays visible while the conversation carries on without it.
				const detail =
					e.event === "started"
						? JSON.stringify(e.args ?? {})
						: (e.error ?? JSON.stringify(e.result ?? null));
				setBody(bubble(`${e.name} · ${e.event}`, "tool"), detail);
				return;
			}
			if (e.role === "context") {
				// A backend note (SPEC §18.8) or an agent row (§18.14), keyed
				// like every durable line so a replay re-renders it instead of
				// adding a second one.
				if (context.has(e.messageId)) return;
				const who = e.source === "agent" ? (e.kind === "status" ? "agent status" : "agent") : "backend";
				const line = bubble(`${who} · ${e.mode}`, "tool");
				context.add(e.messageId);
				setBody(line, e.text);
				return;
			}
			let line = assistant.get(e.messageId);
			if (!line) {
				line = bubble("liaison", "assistant");
				assistant.set(e.messageId, line);
				// An interim still pending when the liaison starts a reply was
				// spoken over that reply, not before it. Re-appending moves it
				// below the new line, so the page stays in the order the words
				// were said even if the interim is never committed.
				if (interim) container.appendChild(interim);
			}
			setBody(line, e.text);
			if (e.interrupted) line.classList.add("interrupted");
		},
		line(who: string, cls: string, text: string): void {
			setBody(bubble(who, cls), text);
		},
		clear(): void {
			container.replaceChildren();
			assistant.clear();
			user.clear();
			context.clear();
			interim = null;
		},
	};
}
